<h2>Demo of ez-button BooleanReactiveInput</h2>
<p>
  The ez-button uses a BooleanReactiveInput for the disabled property. This may be the start of broader system for configurable components.<br />
  I.e. if you want to make a re-usable and easily configurable component, then some inputs may benefit from being a XReactiveInput as opposed<br/>
  to just an X (boolean, string, number, date) so that when used as part of a configuration object, updates to the property will propogate as expected.<br />
  An XReactiveInput behaves the same as the native X when used directly as an input in an HTML template.<br />
  Additionally, the ReactiveInput property can be passed as part of a config object and still be reactive (depending on how you do it).
</p>

<h3>NOTE: This might all be unecessary by instead using Angular's new <a href="https://angular.dev/api/core/InputSignal" target="_blank">InputSignal</a> but I didn't know about that when I created the ReactiveInputs.</h3>
<!-- Toggle Controls Above Table -->
 <ui-section class="app-mb">
  <ui-block width="1x" class="my-block flex flex-col ui-gap">
      <ui-button (click)="isDisabled = !isDisabled">Toggle isDisabled</ui-button>
      <ui-button (click)="isDisabledSig.set(!isDisabledSig())">Toggle isDisabledSig</ui-button>
      <span>Observable on a timer</span>
    </ui-block>

  <ui-block  width="2x" class="my-block flex flex-col ui-gap">
    <div>isDisabled: <b>{{ isDisabled }}</b></div>
    <div>isDisabledSig(): <b>{{ isDisabledSig() }}</b></div>
    <div>isDisabled$ | async: <b>{{ isDisabled$ | async }}</b></div>
  </ui-block>
</ui-section >

<table class="styled-table">
  <thead>
    <tr>
      <th>Type</th>
      <th>Native Button</th>
      <th>Direct Inputs</th>
      <th>Config</th>
      <th>Notes</th>
    </tr>
  </thead>
  <tbody>
    <!-- Constant -->
    <tr>
      <td>Constant</td>
      <td>
        <div class="flex flex-col ui-gap">
          <button [disabled]="true">Button</button>
          <pre><code [innerText]="nativeConstantButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <ui-button [disabled]="true">ConfigButton</ui-button>
          <pre><code [innerText]="configConstantButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <div class="flex flex-col ui-gap">
            <ui-button [config]="config1">ConfigButton</ui-button>
            <pre><code [innerText]="configHtmlSnippet1"></code></pre>
          </div>
          <div class="flex flex-col ui-gap">
            <pre><code [innerText]="configTsSnippet1"></code></pre>
          </div>
        </div>
      </td>
      <td>Constant value never changes. Buttons are always disabled</td>
    </tr>

    <!-- Variable -->
    <tr>
      <td>Variable</td>
      <td>
        <div class="flex flex-col ui-gap">
          <button [disabled]="isDisabled">Button</button>
          <pre><code [innerText]="nativeStaticButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <ui-button [disabled]="isDisabled">ConfigButton</ui-button>
          <pre><code [innerText]="configStaticButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <div class="flex flex-col ui-gap">
            <span class="text-red-500"><ui-button [config]="config2"></ui-button> Doesn't work</span>
            <pre><code [innerText]="configHtmlSnippet2"></code></pre>
          </div>
          <div class="flex flex-col ui-gap">
            <pre><code [innerText]="configTsSnippet2"></code></pre>
          </div>
        </div>
      </td>
      <td>
        <p><b>Variables are easy and work well as direct inputs using Angular's built-in change detection mechanism but they don't work for config objects.</b></p>
        <p>This doesn't work in the config version because config2 is never reloaded to the button, thus changes to isDisabled are not picked up. There are ways to make this work but might not be worth the effort.</p>
      </td>
    </tr>

    <!-- Function -->
    <tr>
      <td>Function</td>
      <td>
        <div class="flex flex-col ui-gap">
          <button [disabled]="getIsDisabled()">Button</button>
          <pre><code [innerText]="nativeFunctionButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <ui-button [disabled]="getIsDisabled()">ConfigButton</ui-button>
          <pre><code [innerText]="configFunctionButtonSnippet"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <div class="flex flex-col ui-gap">
            <ui-button [config]="config3"></ui-button>
            <pre><code [innerText]="configHtmlSnippet3"></code></pre>
          </div>
          <div class="flex flex-col ui-gap">
            <pre><code [innerText]="configTsSnippet3"></code></pre>
          </div>
        </div>
      </td>
      <td>
        <p><b>Functions work for all use cases but are inefficient because they rely on timers or get reevaluated on every change cycle.</b></p>
        <p>Polling-based change detection—delay in updates, not ideal. Prefer signal if possible. <br />TODO: (maybe) change this to fire on change
          detection instead of a timer. Need to move util functions to an injection context, like maybe a base class.</p>
      </td>
    </tr>

    <!-- Signal -->
    <tr>
      <td>Signal</td>
      <td>
        <div class="flex flex-col ui-gap">
          <button [disabled]="isDisabledSig()">Button</button>
          <pre><code [innerText]="nativeSignalButtonSnippet1"></code></pre>

          <button [disabled]="isDisabledSig()">Button (doesn't work)</button>
          <pre><code [innerText]="nativeSignalButtonSnippet2"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <ui-button [disabled]="isDisabledSig()">ConfigButton ✅</ui-button>
          <pre><code [innerText]="configSignalButtonSnippet1"></code></pre>
          <span class="text-red-500"><ui-button [disabled]="isDisabledSig()">ConfigButton () ❌</ui-button> Works but not efficient</span>
          <pre><code [innerText]="configSignalButtonSnippet2"></code></pre>
        </div>
      </td>
      <td>
        <div class="flex flex-col ui-gap">
          <div class="flex flex-col ui-gap">
            <ui-button [config]="config4a"></ui-button>
            <pre><code [innerText]="configHtmlSnippet4a"></code></pre>
            <div class="flex flex-col ui-gap">
              <pre><code [innerText]="configTsSnippet4a"></code></pre>
            </div>
            <span class="text-red-500"><ui-button [config]="config4b"></ui-button> Works but not efficient</span>
            <pre><code [innerText]="configHtmlSnippet4b"></code></pre>
            <div class="flex flex-col ui-gap">
              <pre><code [innerText]="configTsSnippet4b"></code></pre>
            </div>
          </div>
          <!-- <div class="flex flex-col ui-gap">
            <pre><code [innerText]="configTsSnippet4a"></code></pre>
          </div> -->
        </div>
      </td>
      <td>
        <p><b>Signals are the most reliable and efficient way to have reactive inputs accross all use-cases when used properly.</b></p>
        <p>Signal works natively with `()`. With a ReactiveInput, it can be passed directly (by reference) as an input and used as a config property which is more efficient.</p>
        <table style="border-collapse: collapse; width: 100%;">
          <thead>
            <tr>
              <th style="border: 1px solid #ccc; padding: 8px;">Version</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Reactive?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Tracked?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Efficient?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">CD-Evaluated?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #ccc; padding: 8px;"><code>isDisabledSig</code></td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Highly</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Only when needed</td>
            </tr>
            <tr>
              <td style="border: 1px solid #ccc; padding: 8px;"><code>isDisabledSig()</code></td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ No</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ No</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ Less so</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ Always</td>
            </tr>
          </tbody>
        </table>

        <!-- <table style="border-collapse: collapse; width: 100%;">
          <thead>
            <tr>
              <th style="border: 1px solid #ccc; padding: 8px;">Version</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Reactive?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Tracked?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Efficient?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">CD-Evaluated?</th>
              <th style="border: 1px solid #ccc; padding: 8px;">Works with native components?</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #ccc; padding: 8px;"><code>isDisabledSig</code></td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Highly</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Only when needed</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes</td>
            </tr>
            <tr>
              <td style="border: 1px solid #ccc; padding: 8px;"><code>isDisabledSig()</code></td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ No</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ No</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ Less so</td>
              <td style="border: 1px solid #ccc; padding: 8px;">❌ Always</td>
              <td style="border: 1px solid #ccc; padding: 8px;">✅ Yes (but inefficient)</td>
            </tr>
          </tbody>
        </table> -->
      </td>
    </tr>
<tr>
  <td>Observable</td>

  <!-- Native Button -->
  <td>
    <div class="flex flex-col ui-gap">
      <button [disabled]="(isDisabled$ | async)">Button</button>
      <pre><code [innerText]="nativeObservableButtonSnippet"></code></pre>
    </div>
  </td>

  <!-- Direct Inputs -->
  <td>
    <div class="flex flex-col ui-gap">
      <ui-button [disabled]="(isDisabled$ | async) || false">ConfigButton</ui-button>
      <pre><code [innerText]="configObservableButtonSnippet"></code></pre>
    </div>
  </td>

  <!-- Config -->
  <td>
    <div class="flex flex-col ui-gap">
      <div class="flex flex-col ui-gap">
        <ui-button [config]="config5"></ui-button>
        <pre><code [innerText]="configHtmlSnippet5"></code></pre>
      </div>
      <div class="flex flex-col ui-gap">
        <pre><code [innerText]="configTsSnippet5"></code></pre>
      </div>
    </div>
  </td>

  <!-- Notes -->
  <td>
    <p><b>While reactive, observables create a lot of overhead and can cause memory leaks if not cleaned up properly.</b></p>
    <p>ReactiveInputs don't do anything special with observables. They work the same as the native properties when used
      directly as an input (i.e. must use | async). When using an observable as a configuration property, convert to a signal first with toSignal().
      NOTE: Do not use toSignal() in the template, that doesn't work.</p>
  </td>
</tr>

  </tbody>
</table>
